import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { debounce } from 'lodash';
import { buildUrl, truncateKeywordSpaces, toQueryString, removeEmpty } from 'utils';
import {
  Box,
  Grid,
  Paper,
  Button,
  Tooltip,
  Container,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Edit as EditIcon, Search as SearchIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Title from 'components/Title';
import Layout from 'components/Layout';
import { creditTypesActions } from 'state/modules/creditTypes';
import { Table } from 'components/UI';
import { useHistory } from 'react-router-dom';
import { FETCH_STATE } from 'constants';
import Can from 'components/Can';
import { SearchField } from 'components/FormFields';
import { CreateCreditTypeModal } from 'components/Modals';
import { useConfirm, useQuery } from 'hooks';
import PropTypes from 'prop-types';

const ActionsMenu = ({ row, handleDelete, handleUpdate }) => {
  const [toggleDeleteCreditType, DeleteCreditType] = useConfirm({
    title: i18n.t('credit_types.delete'),
    message: i18n.t('credit_types.delete_description'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: () => handleDelete(row),
  });

  return (
    <Box display="flex" justifyContent="center">
      <Can I="update" a="invoice_item_type">
        <Tooltip title={i18n.t('credit_types.edit')}>
          <IconButton
            variant="contained"
            aria-haspopup="true"
            aria-controls="card-menu"
            aria-label={i18n.t('credit_types.edit')}
            size="medium"
            onClick={() => handleUpdate(row)}
            data-cy={`credit-types-edit-${row?.name}`}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Can>
      <Can I="delete" a="invoice_item_type">
        <Tooltip title={i18n.t('credit_types.delete')}>
          <IconButton
            variant="contained"
            aria-haspopup="true"
            aria-controls="card-menu"
            aria-label={i18n.t('credit_types.delete')}
            size="medium"
            onClick={toggleDeleteCreditType}
            data-cy={`credit-types-delete-${row?.name}`}
          >
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Can>

      <DeleteCreditType />
    </Box>
  );
};

ActionsMenu.propTypes = {
  row: PropTypes.object.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleUpdate: PropTypes.func.isRequired,
};

const fixedParams = { active: true, status: 'ACTIVE', is_account_level: true };

const CreditTypeList = () => {
  const urlParams = useQuery();
  const [params, setParams] = useState({ page: 1, limit: 10, ...urlParams });
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPromoId, setCurrentPromoId] = useState(null);

  // We use multiple selectors to avoid table rerender / consider using reselect.
  const data = useSelector((state) => state.creditTypes.creditTypes);
  const fetchingCreditTypes = useSelector((state) => state.creditTypes.fetchingCreditTypes);
  const pagination = useSelector((state) => state.creditTypes.listPagination);
  const createCreditType = useSelector((state) => state.creditTypes.createCreditType);
  const deleteCreditType = useSelector((state) => state.creditTypes.deleteCreditType);
  const updateCreditType = useSelector((state) => state.creditTypes.updateCreditType);

  const loading = fetchingCreditTypes === FETCH_STATE.LOADING;
  const refetch =
    createCreditType === FETCH_STATE.DONE ||
    deleteCreditType === FETCH_STATE.DONE ||
    updateCreditType === FETCH_STATE.DONE;

  const fetchCreditTypes = useCallback(
    (params) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        creditTypesActions.fetchCreditTypes(
          { ...params, ...fixedParams },
          { signal: abortController?.current?.signal }
        )
      );
    },
    [dispatch]
  );

  const fetchCreditTypesDebounced = useMemo(
    () => debounce((params) => fetchCreditTypes(params), 500),
    [fetchCreditTypes]
  );

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  const handleSearch = (name, value) => {
    const searchParams = {
      ...params,
      page: 1,
      [name]: truncateKeywordSpaces(value),
    };
    handleParams({ ...searchParams });
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  const handlePerRowsChange = (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  const fetchCreditType = useCallback(
    (id) => {
      dispatch(creditTypesActions.fetchCreditTypeById(id));
    },
    [dispatch]
  );

  const handleParams = (params) => {
    const cleanParams = removeEmpty(params);
    setParams(cleanParams);
    const qs = toQueryString(cleanParams);
    history.replace(buildUrl(`/credit-types${qs}`));
  };

  useEffect(() => {
    if (currentPromoId) {
      fetchCreditType(currentPromoId);
    }
  }, [fetchCreditType, currentPromoId]);

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/credit-types/${row?.id}`));
  };

  const toggleModal = () => {
    setShowCreateModal((state) => !state);
  };

  const handleCreate = () => {
    setCurrentPromoId(null);
    toggleModal();
  };

  const handleDelete = (row) => {
    dispatch(creditTypesActions.deleteCreditType(row.id));
  };

  const handleUpdate = (row) => {
    setCurrentPromoId(row.id);
    toggleModal();
  };

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '4rem',
      sortable: true,
    },
    {
      name: i18n.t('common.name'),
      selector: 'name',
      format: (row) => row.name || '--',
      grow: 2,
      sortable: true,
    },
    {
      name: i18n.t('common.description'),
      selector: 'description',
      format: (row) => row.description || '--',
      grow: 2,
    },
    {
      grow: 1,
      center: true,
      name: i18n.t('common.actions'),
      cell: (row) => (
        <ActionsMenu row={row} handleDelete={handleDelete} handleUpdate={handleUpdate} />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      sortable: false,
      minWidth: '9rem',
    },
  ];

  //Fetch debounced on params change
  useEffect(() => {
    if (isMounted.current) {
      fetchCreditTypesDebounced({ ...params });
    } else {
      isMounted.current = true;
    }
  }, [fetchCreditTypes, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchCreditTypes({ ...params });
  }, [fetchCreditTypes, params]);

  // Fetch right away on create or delete success
  useEffect(() => {
    if (refetch) {
      fetchCreditTypes({ ...params });
    }
  }, [fetchCreditTypes, params, refetch]);

  return (
    <Layout title={i18n.t('credit_types.title')}>
      <Container sx={{ minWidth: '100%', margin: 0 }}>
        <Grid container spacing={2}>
          <Grid item size={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Title title={i18n.t('credit_types.title')} />
              </Grid>
              <Grid item>
                <Can I="create" a="invoice_item_type">
                  <Button
                    onClick={handleCreate}
                    sx={{ textTransform: 'capitalize' }}
                    variant="contained"
                    color="primary"
                    data-cy={'create-credit-type-btn'}
                  >
                    {i18n.t('credit_types.create')}
                  </Button>
                </Can>
              </Grid>
            </Grid>
          </Grid>
          <Grid item size={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="flex-end"
              justifyContent="space-between"
            >
              <Grid item size={{ xs: 3 }}>
                <SearchField
                  margin="none"
                  label={`${i18n.t('credit_types.search')}`}
                  field={{ onChange: handleSearch, name: 'keyword' }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        sx={{
                          '& svg': {
                            color: (theme) => theme.palette.secondary.light,
                            fontSize: (theme) => theme.typography.pxToRem(14),
                          },
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  data-cy="search-credit-type-input"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item size={12}>
            <Paper elevation={2}>
              <Table
                data={data}
                columns={columns}
                onSort={handleSort}
                loading={loading}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                page={params?.page}
                limit={params?.limit}
                defaultSortField={params?.sort_field}
                defaultSortAsc={!params?.sort_desc}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <CreateCreditTypeModal id={currentPromoId} open={showCreateModal} toggleModal={toggleModal} />
    </Layout>
  );
};

export { CreditTypeList };
